import { RoomStateRubricResult } from '@breakoutlearning/firebase-repository/models/RoomStateRubricResult'
import { SlideDeckRubricTestResult } from '@breakoutlearning/firebase-repository/models/SlideDeckRubricTestResult'
import type { SlideRubric } from '@breakoutlearning/firebase-repository/models/SlideRubric'
import { RubricType } from '@breakoutlearning/firebase-repository/types'
import classNames from 'classnames'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { CheckIcon } from 'components/icons/Check'
import { Close } from 'components/icons/Close'
import { useRubricTypes } from 'hooks/useRubricTypes'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'

export const RubricScoreWidgetBox = observer(function RubricScoreWidgetBox({
  rubricType,
  rubricText,
  rubricResult,
}: {
  rubricType?: RubricType
  rubricText: string
  rubricResult: RoomStateRubricResult | SlideDeckRubricTestResult | number
}) {
  const rubricTypeObj = useRubricTypes().getRubricByType(rubricType)

  const score = useMemo(() => {
    if (
      rubricResult instanceof SlideDeckRubricTestResult ||
      rubricResult instanceof RoomStateRubricResult
    )
      return rubricResult.data.score
    return rubricResult
  }, [rubricResult])

  const rubricColor = rubricTypeObj.getScoreBgColor(score)
  const rubricColorWithOpacity = rubricTypeObj.getScoreBgColor(score, {
    opacity: 0.3,
  })
  const rubricTextColor = rubricTypeObj.getScoreTextColor(score)

  // if we have a text color defined, don't manipulate the bg color opacity
  // else we use the bg color with opacity for the bg, and the solid color for the text
  const bgColor = rubricTextColor ? rubricColor : rubricColorWithOpacity
  const fgColor = rubricTextColor ?? rubricColor

  let boxElement: JSX.Element
  // we do custom check/cross for pass/fail rubric, rest we just show the score as integer
  if (rubricType === RubricType.passFail) {
    boxElement = (
      <BreakoutTooltip content={rubricText} placement="top">
        <div className="group relative">
          <div
            className="flex h-6 w-6 items-center justify-center rounded"
            style={{ backgroundColor: rubricColorWithOpacity }}
          >
            {/*  if score is 1 show CheckIcon otherwise show CloseIcon */}
            {score === 1 ? (
              <CheckIcon
                className="w-4 text-shadow-white"
                style={{
                  color: rubricColor,
                }}
              />
            ) : (
              <Close
                className="w-4 text-breakout-red text-shadow-white"
                style={{
                  color: rubricColor,
                }}
              />
            )}
          </div>
        </div>
      </BreakoutTooltip>
    )
  } else {
    boxElement = (
      <BreakoutTooltip content={rubricText} placement="top">
        <div className="group relative">
          <div
            className="flex h-6 w-6 items-center justify-center rounded"
            style={{
              backgroundColor: bgColor,
            }}
          >
            <strong
              className={'text-label-small'}
              style={{
                color: fgColor,
              }}
            >
              {score}
            </strong>
          </div>
        </div>
      </BreakoutTooltip>
    )
  }

  return boxElement
})

export const RubricScoreWidget = observer(function RubricScoreWidget({
  rubric,
  rubricResult,
  showTitle = false,
}: {
  rubric: SlideRubric
  rubricResult: RoomStateRubricResult | SlideDeckRubricTestResult | number
  showTitle?: boolean
}) {
  const rubricType = useRubricTypes().getRubricByType(rubric.rubricType)

  const score = useMemo(() => {
    if (
      rubricResult instanceof SlideDeckRubricTestResult ||
      rubricResult instanceof RoomStateRubricResult
    )
      return rubricResult.data.score
    return rubricResult
  }, [rubricResult])

  const boxElement = (
    <RubricScoreWidgetBox
      rubricType={rubric.rubricType}
      rubricText={rubric.data.rubric}
      rubricResult={rubricResult}
    />
  )

  if (!showTitle) return boxElement

  const labelText = rubricType.getScoreLabel(score, rubric)

  return (
    <div className="flex flex-row items-center gap-1 overflow-hidden">
      {boxElement}
      <strong
        className={classNames('truncate text-nowrap text-label-small')}
        //style={{ color: rubricColor }}
      >
        {labelText}
      </strong>
    </div>
  )
})

/**
 * pass the rubric list sorted
 */
export const RubricScoreList = ({
  rubricList,
  rubricResults,
  className,
  onClick,
  wasAloneInExperience,
  userRole,
}: {
  rubricList: SlideRubric[]
  rubricResults: Map<
    SlideRubric,
    RoomStateRubricResult[] | RoomStateRubricResult
  >
  onClick?: () => void
  className?: string
  wasAloneInExperience?: boolean
  userRole: string | null
}) => {
  const types = useRubricTypes()
  const { tt } = useTranslationTyped()

  const items = rubricList.map((rubric, i) => {
    const userMissingAppropriateRole =
      rubric.data.assignmentRoles &&
      rubric.data.assignmentRoles.length &&
      (!userRole || !rubric.data.assignmentRoles.includes(userRole))

    const result = rubricResults.get(rubric)
    const rubricTypeObj = types.getRubricByType(rubric.rubricType)

    const isMissingData = !result || (Array.isArray(result) && !result.length)

    if (
      (isMissingData &&
        rubricTypeObj.excludeSoloExperiences &&
        wasAloneInExperience) ||
      userMissingAppropriateRole
    ) {
      return {
        key: rubric.id + i,
        label: `${rubric.data.rubric}: ${tt.rubric.rubric_not_generated()}`,
        node: <ExcludedSoloExperienceOrRoleLimitedBox key={rubric.id + i} />,
      }
    }

    if (isMissingData) {
      return {
        key: rubric.id + i,
        label: `${rubric.data.rubric}: ${tt.rubric.rubric_data_missing()}`,
        node: <DataMissingBox key={rubric.id + i} />,
      }
    }

    const resultDeconstructed = Array.isArray(result) ? result[0] : result
    const score =
      resultDeconstructed instanceof RoomStateRubricResult
        ? resultDeconstructed.data.score
        : resultDeconstructed
    const scoreLabel =
      rubric.rubricType === RubricType.passFail
        ? rubricTypeObj.getScoreLabel(score, rubric) || String(score)
        : String(score)

    return {
      key: rubric.id + i,
      label: `${rubric.data.rubric}: ${scoreLabel}`,
      node: (
        <span key={rubric.id + i} data-testid="rubric-score-widget">
          <RubricScoreWidget
            rubric={rubric}
            rubricResult={resultDeconstructed}
          />
        </span>
      ),
    }
  })

  const sharedClassName = classNames(
    'flex w-min flex-row items-center gap-1 rounded-lg border border-transparent p-1',
    className
  )

  if (!onClick) {
    return (
      <div className={sharedClassName}>{items.map((item) => item.node)}</div>
    )
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="rubric-score-list"
      aria-haspopup="dialog"
      aria-label={[
        tt.instructor_assignment.rubric_assessment(),
        items.map((item) => item.label).join(', '),
      ].join(': ')}
      className={classNames(
        sharedClassName,
        'focus-ring-floating cursor-pointer hover:border hover:border-outline-light hover:bg-surface-dim'
      )}
    >
      {items.map((item) => item.node)}
    </button>
  )
}

export function ExcludedSoloExperienceOrRoleLimitedBox() {
  const { tt } = useTranslationTyped()
  return (
    <BreakoutTooltip content={tt.rubric.rubric_not_generated()} placement="top">
      <div
        className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-surface-dim text-center text-label-medium text-on-surface-var"
        data-testid="rubric-role-limited-box"
      >
        -
      </div>
    </BreakoutTooltip>
  )
}

export function DataMissingBox() {
  const { tt } = useTranslationTyped()
  return (
    <BreakoutTooltip content={tt.rubric.rubric_data_missing()} placement="top">
      <div
        className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-on-surface-disabled text-center text-label-medium text-on-surface"
        data-testid="rubric-data-missing-box"
      >
        ?
      </div>
    </BreakoutTooltip>
  )
}
